import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { ChevronDown, Check } from 'lucide-react'
import RoomTypeTreeRow from './RoomTypeTreeRow'
import HotelSectionHeader from '@/components/groups/HotelSectionHeader'

interface MultiSelectOption {
  id: number
  name: string
  depth?: number
  isParent?: boolean
  childCount?: number
  isLastChild?: boolean
  spacedAbove?: boolean
  /** Backend says this room isn't being priced — renders a small muted marker. */
  notPriced?: boolean
  /** Groups: which hotel this room belongs to, and whether that hotel leads the group. */
  hotelName?: string | null
  hotelPropertyId?: number | null
  isLeadHotel?: boolean
  /** Groups: the first room of a new hotel's block — draws a heading above it. */
  startsHotelBlock?: boolean
  /** Groups: this room follows a parent in another hotel, so the link is stated, not drawn. */
  followsName?: string | null
  followsPropertyName?: string | null
}

interface MultiSelectDropdownProps {
  label: string
  options: MultiSelectOption[]
  selectedIds: number[]
  onChange: (ids: number[]) => void
}

export default function MultiSelectDropdown({
  label,
  options,
  selectedIds,
  onChange,
}: MultiSelectDropdownProps) {
  const [open, setOpen] = useState(false)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  const updatePos = useCallback(() => {
    if (!buttonRef.current) return
    const rect = buttonRef.current.getBoundingClientRect()
    setPos({ top: rect.bottom + 4, left: rect.left })
  }, [])

  useEffect(() => {
    if (!open) return
    updatePos()
    const handler = (e: MouseEvent) => {
      const target = e.target as Node
      if (
        buttonRef.current?.contains(target) ||
        panelRef.current?.contains(target)
      ) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    window.addEventListener('scroll', updatePos, true)
    window.addEventListener('resize', updatePos)
    return () => {
      document.removeEventListener('mousedown', handler)
      window.removeEventListener('scroll', updatePos, true)
      window.removeEventListener('resize', updatePos)
    }
  }, [open, updatePos])

  const allSelected = selectedIds.length === options.length
  const summary = allSelected
    ? 'All'
    : `${selectedIds.length}/${options.length}`

  const toggleItem = (id: number) => {
    if (selectedIds.includes(id)) {
      if (selectedIds.length <= 1) return
      onChange(selectedIds.filter((x) => x !== id))
    } else {
      onChange([...selectedIds, id])
    }
  }

  const selectAll = () => onChange(options.map((o) => o.id))
  const deselectAll = () => {
    if (options.length > 0) onChange([options[0].id])
  }

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={cn(
          'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-medium transition-all',
          open
            ? 'border-primary/30 bg-primary/5 text-primary'
            : 'border-border/60 bg-white text-muted-foreground hover:text-foreground hover:border-border'
        )}
      >
        <span className="uppercase tracking-wider">{label}</span>
        <span className="font-semibold text-foreground">{summary}</span>
        <ChevronDown className={cn('h-3 w-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] min-w-[220px] rounded-lg border border-border/60 bg-white p-1.5 shadow-lg"
          style={{ top: pos.top, left: pos.left }}
        >
          {/* Select all / Deselect all */}
          <div className="flex items-center justify-between px-2 py-1.5 border-b border-border/40 mb-1">
            <button
              type="button"
              onClick={selectAll}
              className="text-[10px] font-medium text-primary hover:text-primary/80 uppercase tracking-wider"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={deselectAll}
              className="text-[10px] font-medium text-muted-foreground hover:text-foreground uppercase tracking-wider"
            >
              Deselect All
            </button>
          </div>

          {/* Options */}
          <div className="max-h-[280px] overflow-y-auto">
            {options.map((option) => {
              const checked = selectedIds.includes(option.id)
              return (
                <React.Fragment key={option.id}>
                {/* A hotel heading, so a unit-wide list reads as blocks rather than as one long
                    run of room names. Absent unless the list actually spans hotels. A HEADING, not
                    a chip — a capped chip clipped "Modern Studios Close To Hyde Park" in a panel
                    with room to spare. */}
                {option.startsHotelBlock && option.hotelName && (
                  <div className="mt-1.5 border-t border-border/40 px-2 pb-1 pt-1.5">
                    <HotelSectionHeader
                      name={option.hotelName}
                      propertyId={option.hotelPropertyId}
                      isLead={option.isLeadHotel}
                    />
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => toggleItem(option.id)}
                  className={cn(
                    'group flex w-full items-stretch gap-2 rounded-md px-2 py-1.5 text-xs transition-colors',
                    option.spacedAbove && 'mt-1',
                    checked
                      ? 'text-foreground'
                      : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground'
                  )}
                >
                  <span className="flex items-center">
                    <span className={cn(
                      'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                      checked
                        ? 'border-primary bg-primary text-primary-foreground'
                        : 'border-border'
                    )}>
                      {checked && <Check className="h-3 w-3" />}
                    </span>
                  </span>
                  <RoomTypeTreeRow
                    name={option.name}
                    depth={option.depth}
                    isParent={option.isParent}
                    childCount={option.childCount}
                    isLastChild={option.isLastChild}
                    selected={checked}
                    followsName={option.followsName}
                    followsPropertyName={option.followsPropertyName}
                  />
                  {option.notPriced && (
                    <span className="shrink-0 self-center rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground/80">
                      Not priced
                    </span>
                  )}
                </button>
                </React.Fragment>
              )
            })}
          </div>
        </div>,
        document.body
      )}
    </>
  )
}
